import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Th = ({ children }) => (
  <th className="text-left text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3">{children}</th>
);

export function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pendente", color: "bg-amber-100 text-amber-800" },
    approved: { label: "Aprovado", color: "bg-emerald-100 text-emerald-800" },
    rejected: { label: "Rejeitado", color: "bg-red-100 text-red-800" },
  };
  const s = map[status] || { label: status, color: "bg-gray-100 text-gray-800" };
  return <span className={`text-xs px-2 py-1 ${s.color}`}>{s.label}</span>;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function PhotoThumb({ member, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const initials = (member.name || member.email || "?").trim().split(/\s+/).slice(0, 2)
    .map((s) => s[0] || "").join("").toUpperCase() || "?";

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error("Use JPG, PNG ou WEBP."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Máx 5 MB."); return; }
    (async () => {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/admin/members/${member.id}/photo`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Foto carregada.");
        onUploaded?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar.");
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    })();
  };

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        title={member.picture ? "Substituir fotografia" : "Carregar fotografia"}
        className="h-10 w-10 rounded-full overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--accent))] transition-colors flex items-center justify-center"
        data-testid={`admin-photo-${member.id}`}
      >
        {member.picture ? (
          <img src={member.picture} alt={member.name || ""} className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs font-medium text-[hsl(var(--primary))]">{initials}</span>
        )}
        {uploading && (
          <span className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
            <span className="text-white text-[10px]">···</span>
          </span>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onPick}
        className="hidden"
      />
    </div>
  );
}

export function JoinedAtEditor({ value, onSave, disabled, testid }) {
  const [v, setV] = useState(value || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value || ""); }, [value]);

  const commit = () => {
    setEditing(false);
    if ((v || null) === (value || null)) return;
    onSave(v || null);
  };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        title={disabled ? "Apenas para sócios aprovados" : "Clique para editar"}
        className={`text-xs px-2 py-1 border border-transparent rounded-sm ${
          disabled
            ? "text-[hsl(var(--muted-foreground))] cursor-not-allowed"
            : "hover:border-[hsl(var(--accent))] cursor-text"
        }`}
        data-testid={testid}
      >
        {value ? new Date(value).toLocaleDateString("pt-PT") : <span className="italic text-[hsl(var(--muted-foreground))]">— definir —</span>}
      </button>
    );
  }
  return (
    <input
      type="date"
      autoFocus
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setV(value || ""); setEditing(false); } }}
      className="bg-white border border-[hsl(var(--accent))] text-xs px-2 py-1 focus:outline-none"
      data-testid={`${testid}-input`}
    />
  );
}
